import React, { useState } from "react";
import { useViewport } from 'components/general/useViewPort'
import Label from "components/general/Label"
import { getPhaseRSC } from 'helpers/rsc'
import { getCurrentRound } from "components/sports/glf/helper/GLFHelper";
import ButtonGroup from 'react-bootstrap/ButtonGroup';
import Button from 'react-bootstrap/Button';
import ButtonToolbar from 'react-bootstrap/ButtonToolbar';
import Stack from 'react-bootstrap/Stack';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExpand, faPlus, faAngleLeft, faAngleRight
} from "@fortawesome/free-solid-svg-icons";
import 'styles/sports/glf.scss';
import { useUrlFriendlyToParameters } from "helpers/urlFriendly";
import FavButton from "components/sports/glf/component/FavButton"

function getInitialCurrentHole(participant, currentUnitData) {
  const participantData = currentUnitData?.results?.items?.find(r => r.participant.code === participant);
  const current_hole = participantData?.extendedResults?.find(er => er.er_code === 'CURRENT')?.er_value;
  const complete_hole = participantData?.extendedResults?.find(er => er.er_code === 'COMPLETE')?.er_value;
  return (current_hole)
    ? current_hole
    : (complete_hole)
      ? complete_hole
      : "1"
}

const ShotTracker = ({ participant, cumulativeData, unitsData, statsData, favData, setFavData }) => {
  const { breakPoint } = useViewport();
  const isMobile = (breakPoint === "sm" || breakPoint === "xs")
  const { mirs_rsc } = useUrlFriendlyToParameters()
  let _phase = getPhaseRSC(mirs_rsc);
  const _units = [
    { rsc: _phase + '000100--', round: "1" }
    , { rsc: _phase + '000200--', round: "2" }
    , { rsc: _phase + '000300--', round: "3" }
    , { rsc: _phase + '000400--', round: "4" }
  ]

  let _currentUnit = getCurrentRound(cumulativeData?.cumulativeResults?.progress?.lastUnit, _phase)

  const [currentUnitData, setCurrentUnitData] = useState(unitsData.find(item => item.data?.config?.unit === _currentUnit)?.data);
  const [currentUnit, setCurrentUnit] = useState(_currentUnit);
  const [currentRound, setCurrentRound] = useState(_units.find(u => u.rsc === _currentUnit)?.round);
  const [currentHole, setCurrentHole] = useState(getInitialCurrentHole(participant, currentUnitData));
  const [currentMetric, setMetric] = useState("metric")
  const [currentZoom, setZoom] = useState("0") // 0: normal, 1: zoom x2, 3:zoom to pin

  return (
    <>
      <ButtonToolbar>
        <ButtonGroupRound
          currentUnit={currentUnit}
          setCurrentUnit={setCurrentUnit}
          setCurrentRound={setCurrentRound}
          setCurrentUnitData={setCurrentUnitData}
          _units={_units}
          unitsData={unitsData}
        />
        <ButtonGroupHole
          currentHole={currentHole}
          setCurrentHole={setCurrentHole}
          currentUnitData={currentUnitData}
          isMobile={isMobile}
        />
        <ButtonGroupMetric
          currentMetric={currentMetric}
          setMetric={setMetric}
        />
        {
          isMobile && <Stack direction="horizontal" className="align-items-end">
                <FavButton participant={participant} favData={favData} setFavData={setFavData}/>
              </Stack>
        }
      </ButtonToolbar>
      <HoleInfo
        currentMetric={currentMetric}
        participant={participant}
        currentRound={currentRound}
        currentHole={currentHole}
        currentUnitData={currentUnitData}
        currentUnit={currentUnit}
        statsData={statsData}
        currentZoom={currentZoom}
        setZoom={setZoom}
        isMobile={isMobile}
      />
      <ScoreCard
        currentMetric={currentMetric}
        participant={participant}
        unitsData={unitsData}
        _units={_units}
        cumulativeData={cumulativeData}
        isMobile={isMobile}
      />
    </>
  )
};

export default ShotTracker;

const HoleInfo = ({ currentMetric, participant, currentRound, currentHole, currentUnitData, currentUnit, statsData, currentZoom, setZoom, isMobile }) => {
  const holeConfig = currentUnitData?.config?.extendedConfig?.find(hole => hole.code === 'HOLE' && hole.pos === currentHole)
  const par = holeConfig?.value
  const meters = holeConfig?.extension?.find(ext => ext.code === 'DISTANCE_TEE_BOX' && ext.pos === "0")?.value
  const yards = holeConfig?.extension?.find(ext => ext.code === 'DISTANCE_TEE_BOX' && ext.pos === "1")?.value
  const holeRank = statsData.find(d => d.data?.stats?.find(x => x !== undefined)?.rsc === currentUnit)
    ?.data?.stats?.find(x => x !== undefined)?.statsItems //esto es así pq la query devuelve un array de un solo elemento
    .find(s => s.code === 'HOLE' && s.pos === currentHole)?.extendedStats
    .find(e => e.code === 'DIFFICULTY')?.value;
  return (
    <>
      <Stack className="mt-1 align-items-end" direction={currentZoom === "0" ? "horizontal" : "vertical"}>
        <Stack direction="horizontal" className="align-items-end">
          <HoleImage participant={participant}
            currentHole={currentHole}
            currentUnitData={currentUnitData}
            currentZoom={currentZoom}
            currentMetric={currentMetric}
            isMobile={isMobile} />
          <ButtonGroupZoom currentZoom={currentZoom} setZoom={setZoom} />
        </Stack>
        <Stack direction="vertical" className="mt-1 align-items-start">
          <div className="p-1">
            <strong><Label data="HeaderRound" /> {currentRound}</strong>
          </div>
          <div className="p-1">
            <strong><Label data="HeaderHole" /> {currentHole} - <Label data="HeaderPar" /> {par}</strong>
          </div>
          {(currentMetric === 'metric')
            ? (
              <div className="p-1">
                <strong>{meters} <Label data="HeaderMeters" /></strong>
              </div>
            ) : (
              <div className="p-1">
                <strong>{yards} <Label data="HeaderYards" /></strong>
              </div>
            )
          }
          <div className="p-1">
            <strong><Label data="LabelHoleRank" /> {holeRank}</strong>
          </div>
        </Stack>
      </Stack>
    </>
  )
};

const HoleImage = ({ participant, currentHole, currentUnitData, currentZoom, currentMetric, isMobile }) => {
  const currentImage = currentUnitData?.images?.find(img => img.imgPos === currentHole)
  const img_url = currentImage?.imagePath
  var image_width = isMobile ? 175 : 375
  var image_height = isMobile ? 82 : 175
  const hole = currentUnitData?.config?.extendedConfig?.find(hole => hole.code === 'HOLE' && hole.pos === currentHole)
  const pin_x_perc = hole?.extension?.find(ext => ext.code === 'PIN_X' && ext.pos === "1")?.value
  const pin_y_perc = hole?.extension?.find(ext => ext.code === 'PIN_Y' && ext.pos === "1")?.value
  const tee_x_perc = hole?.extension?.find(ext => ext.code === 'TEE_X' && ext.pos === "1")?.value
  const tee_y_perc = hole?.extension?.find(ext => ext.code === 'TEE_Y' && ext.pos === "1")?.value

  var viewbox_x = 0;
  var viewbox_y = 0;
  var viewbox_width = image_width;
  var viewbox_height = image_height;
  var svg_width = image_width;
  var svg_height = image_height;

  switch (currentZoom) {
    case "1":
      image_width = image_width * 2;
      image_height = image_height * 2;
      viewbox_width = image_width;
      viewbox_height = image_height;
      svg_width = image_width;
      svg_height = image_height;
      break;
    case "2":
      image_width = image_width * 3;
      image_height = image_height * 3;
      viewbox_x = ((pin_x_perc * image_width) / 100) - 50;
      viewbox_y = ((pin_y_perc * image_height) / 100) - 50;
      svg_width = 350;
      svg_height = 350;
      viewbox_width = 100;
      viewbox_height = 100;
      break;
  }

  var last_x = (tee_x_perc * image_width) / 100;
  var last_y = (tee_y_perc * image_height) / 100;

  return (
    <svg
      key={`svg-${currentHole}-${participant}`}
      xmlns="http://www.w3.org/2000/svg"
      className="glf-hole-image"
      width={`${svg_width}`}
      height={`${svg_height}`}
      viewBox={`${viewbox_x} ${viewbox_y} ${viewbox_width} ${viewbox_height}`}
    >
      <pattern id={`img-hole-${currentHole}-${participant}`} patternUnits="userSpaceOnUse" x="0" y="0" width={`${image_width}`} height={`${image_height}`}>
        <image href={`${process.env.REACT_APP_SERVER_URL_RESOURCES}/img/${img_url}`} width={`${image_width}`} height={`${image_height}`} />
      </pattern>
      <marker
        id={`dot-${currentHole}-${participant}`}
        viewBox="0 0 10 10" refX="5" refY="5" markerWidth="5" markerHeight="5"
        className="glf-dot-marker">
        <circle cx="5" cy="5" r="5" vectorEffect="non-scaling-size" />
      </marker>
      <symbol
        id={`pin-${currentHole}-${participant}`}
        className="glf-pin-marker" >
        <path d="m8.9935,27.21373c-7.55305,-11.32949 -8.95504,-12.49224 -8.95504,-16.656c0,-5.70344 4.46852,-10.32697 9.98077,-10.32697s9.98077,4.62352 9.98077,10.32697c0,4.16376 -1.40199,5.32651 -8.95504,16.656c-0.49566,0.74085 -1.55586,0.7408 -2.05146,0z" />
      </symbol>
      <path
        d={`M0,0 l0,${image_height} l${image_width},0 l0,-${image_height} l-${image_width},0`}
        fill={`url(#img-hole-${currentHole}-${participant})`}
      />
      {
        currentUnitData?.results?.playByPlay
          ?.find(pbp => pbp?.actions.find(a => a.pbpa_period === `${participant}HOLE_${currentHole.padStart(2, '0')}`))
          ?.actions
          .sort(({ pbpa_order: prevOrder }, { pbpa_order: currOrder }) => prevOrder - currOrder)
          .map(action => {
            const shot = action.pbpa_order;
            const coord_x_perc = action.pbpa_X;
            const coord_y_perc = action.pbpa_Y;
            const coord_x = (coord_x_perc * image_width) / 100;
            const coord_y = (coord_y_perc * image_height) / 100;
            const shot_distance_m = action?.extendedActions.find(ea => ea.pbpea_code === 'SHOT' && ea.pbpea_pos === '0')?.pbpea_value;
            const shot_distance_y = action?.extendedActions.find(ea => ea.pbpea_code === 'SHOT' && ea.pbpea_pos === '1')?.pbpea_value;
            const dtp_m = action?.extendedActions.find(ea => ea.pbpea_code === 'DTP' && ea.pbpea_pos === '0')?.pbpea_value;
            const dtp_y = action?.extendedActions.find(ea => ea.pbpea_code === 'DTP' && ea.pbpea_pos === '1')?.pbpea_value;
            const origin_x = last_x;
            const origin_y = last_y;
            last_x = coord_x;
            last_y = coord_y;

            return (
              <>
                <line
                  key={`line-${currentHole}-${participant})-${shot}`}
                  x1={origin_x} y1={origin_y}
                  x2={coord_x} y2={coord_y}
                  className="glf-line"
                  markerStart={`url(#dot-${currentHole}-${participant})`}
                  markerEnd={`url(#dot-${currentHole}-${participant})`}
                  vectorEffect="non-scaling-stroke" />
                <g transform={`translate(${coord_x}, ${coord_y})`} viewBox="0 0 10 14" width="20" height="28">
                  <use xlinkHref={`#pin-${currentHole}-${participant}`} x="-10" y="-31" />
                  <text className="glf-pin-text">
                    <tspan x="-3" y="-14">{shot}</tspan>
                  </text>
                  <title>
                    <Label data="LabelShotLength" />&nbsp;
                    {(currentMetric === 'metric')
                      ? (<>{shot_distance_m}&nbsp;<Label data="HeaderMeters" /></>)
                      : (<>{shot_distance_y}&nbsp;<Label data="HeaderYards" /></>)
                    }
                    {'\n'}
                    <Label data="LabelDistanceToPin" />&nbsp;
                    {(currentMetric === 'metric')
                      ? (<>{dtp_m}&nbsp;<Label data="HeaderMeters" /></>)
                      : (<>{dtp_y}&nbsp;<Label data="HeaderYards" /></>)
                    }
                  </title>
                </g>
              </>
            )
          })
      }
    </svg>
  )
}

const ButtonGroupMetric = ({ currentMetric, setMetric }) => {
  const types = [
    { type: 'metric', label: <Label data="HeaderMetric" /> },
    { type: 'imperial', label: <Label data="HeaderImperial" /> }
  ];

  const handleClick = (e) => {
    setMetric(e.target.value)
  }
  return (
    <ButtonGroup size="sm" className="align-items-end" onClick={handleClick}>
      {types.map((obj, index) => (
        <Button key={`btn-measureunit-${index}`} className="selector" variant="outline-dark" active={currentMetric === obj.type} value={obj.type} >{obj.label}</Button>
      ))}
    </ButtonGroup>
  );
}

const ButtonGroupRound = ({ currentUnit, setCurrentUnit, setCurrentRound, setCurrentUnitData, _units, unitsData }) => {
  const handleClick = (e) => {
    setCurrentUnit(e.target.value)
    setCurrentRound(e.target.innerText)
    setCurrentUnitData(unitsData.find(item => item.data?.config?.unit === e.target.value)?.data)
  }
  return (
    <>
      <Stack direction="vertical" className="align-items-start">
        <div className="text-left"><strong><Label data="HeaderRound" /></strong></div>
        <div>
          <ButtonGroup size="sm" className="me-2" aria-label="Round" onClick={handleClick}>
            {_units.map(unit => {
              const configData = unitsData.find(u => u.data?.config?.unit === unit.rsc)?.data.config
              return (configData) ? (
                <Button key={`btn-round-${unit.round}`} variant="outline-dark" active={currentUnit === unit.rsc} value={unit.rsc} >{unit.round}</Button>
              ) : ''
            })}
          </ButtonGroup>
        </div>
      </Stack>
    </>
  );
}

function getStartRoundButton(hole, n_buttons) {
  var out = hole - (n_buttons - 1)
  return (out < 1) ? 1 : out
}

function getEndRoundButton(hole, n_buttons) {
  var out = hole + (n_buttons - 1)
  return (out > 18) ? 18 : out
}

const ButtonGroupHole = ({ currentHole, setCurrentHole, currentUnitData, isMobile }) => {
  const n_buttons = isMobile ? 5 : 18;
  const [startButton, setStartButton] = useState(getStartRoundButton(parseInt(currentHole), n_buttons))
  const [endButton, setEndButton] = useState(getEndRoundButton(startButton, n_buttons))

  const handleClick = (e) => {
    setCurrentHole(e.currentTarget.value)
  }
  const handleClickRoundArrow = (e) => {
    if (e.currentTarget.value === "plus" && endButton < 18) {
      var start = startButton + 1
      var end = getEndRoundButton(start, n_buttons)
      setStartButton(start)
      setEndButton(end)
    }
    if (e.currentTarget.value === "minus" && startButton > 1) {
      var start = startButton - 1
      var end = getEndRoundButton(start, n_buttons)
      setStartButton(start)
      setEndButton(end)
    }
  }

  return (
    <>
      <Stack direction="vertical" className="align-items-start">
        <div className="text-left"><strong><Label data="HeaderHole" /></strong></div>
        <div>
          <ButtonGroup size="sm" className="me-2" aria-label="Hole" >
            <>
              {
                currentUnitData?.config?.extendedConfig
                  ?.filter(res => res.code === 'HOLE' && parseInt(res.pos) >= startButton && parseInt(res.pos) <= endButton)
                  .map((hole) => {
                    return (
                      <Button key={`btn-hole-${hole.pos}`} active={currentHole === hole.pos} variant="outline-dark" onClick={handleClick} value={hole.pos}>{hole.pos}</Button>
                    )
                  })
              }
              {isMobile
                ?
                <>
                  <Button variant="none" value="minus" onClick={handleClickRoundArrow}>
                    <FontAwesomeIcon className="font-icon" icon={faAngleLeft} />
                  </Button>
                  <Button variant="none" value="plus" onClick={handleClickRoundArrow}>
                    <FontAwesomeIcon className="font-icon" icon={faAngleRight} />
                  </Button>
                </>
                : ''
              }
            </>
          </ButtonGroup>
        </div>
      </Stack>
    </>
  );
}

const ButtonGroupZoom = ({ currentZoom, setZoom }) => {
  const handleClickZoom = (e) => {
    const value = currentZoom === "0"
      ? e.currentTarget.value
      : currentZoom === e.currentTarget.value
        ? "0"
        : e.currentTarget.value;
    setZoom(value)
  }
  return (
    <ButtonGroup size="sm" vertical className="align-items-end"  >
      <Button variant="outline-dark" active={currentZoom === "1"} value="1" onClick={handleClickZoom}>
        <FontAwesomeIcon className="font-icon" icon={faExpand} />
      </Button>
      <Button variant="outline-dark" active={currentZoom === "2"} value="2" onClick={handleClickZoom}>
        <FontAwesomeIcon className="font-icon" icon={faPlus} />
      </Button>
    </ButtonGroup>
  )
}

const ScoreCard = ({ currentMetric, participant, unitsData, _units, cumulativeData, isMobile }) => {
  let configData = unitsData[0].data;
  const [tableDirection, setTableDirection] = useState("left");

  const handleClickScoreCard = (e) => {
    setTableDirection(e.currentTarget.value)
  }

  return (
    <>
      <Stack direction="horizontal">
        <div className="ms-auto"></div>
        <ButtonGroup size="sm">
          <Button variant="outline-dark" value="left" active={tableDirection === 'left'} onClick={handleClickScoreCard}>
            <FontAwesomeIcon className="font-icon" icon={faAngleLeft} />
          </Button>
          <Button variant="outline-dark" value="right" active={tableDirection === 'right'} onClick={handleClickScoreCard}>
            <FontAwesomeIcon className="font-icon" icon={faAngleRight} />
          </Button>
        </ButtonGroup>
      </Stack>
      {(configData && _units) ?
        (tableDirection === "left") ? (
          buildTable(1, 9, participant, configData, unitsData, _units, cumulativeData, currentMetric, isMobile)
        ) : (
          buildTable(10, 18, participant, configData, unitsData, _units, cumulativeData, currentMetric, isMobile)
        )
        : ''}
      {isMobile
        ? <Stack direction="horizontal" className="justify-content-center" gap={3}>
          <Stack direction="vertical" className="" >
            <div className="glf-card-minus-2 py-1 px-2"><Label data="HeaderEaglesBetter" /></div>
            <div className="border py-1 px-2"><Label data="HeaderPar" /></div>
            <div className="glf-card-plus-2 py-1 px-2"><Label data="HeaderDoubleBogeysOrWorse" /></div>
          </Stack>
          <Stack direction="vertical" className="">
            <div className="glf-card-minus-1 py-1 px-2"><Label data="HeaderBirdies" /></div>
            <div className="glf-card-plus-1 py-1 px-2"><Label data="HeaderBogeys" /></div>
          </Stack>
        </Stack>
        : <Stack direction="horizontal" className="justify-content-center" gap={3}>
          <div className="glf-card-minus-2 py-1 px-2"><Label data="HeaderEaglesBetter" /></div>
          <div className="glf-card-minus-1 py-1 px-2"><Label data="HeaderBirdies" /></div>
          <div className="border py-1 px-2"><Label data="HeaderPar" /></div>
          <div className="glf-card-plus-1 py-1 px-2"><Label data="HeaderBogeys" /></div>
          <div className="glf-card-plus-2 py-1 px-2"><Label data="HeaderDoubleBogeysOrWorse" /></div>
        </Stack>
      }
    </>
  )
}

function getStrikesClass(strikes) {
  var cssClass = "text-center ";
  const parsed = parseInt(strikes);
  if (isNaN(parsed)) { return cssClass }

  if (parsed <= -2) {
    cssClass += "glf-card-minus-2";
  } else if (parsed == -1) {
    cssClass += "glf-card-minus-1";
  } else if (parsed == 1) {
    cssClass += "glf-card-plus-1";
  } else if (parsed >= 2) {
    cssClass += "glf-card-plus-2";
  }

  return cssClass;
}

function buildTable(fromRound, toRound, participant, configData, unitsData, _units, cumulativeData, currentMetric, isMobile) {
  const competitorCumulativeData = cumulativeData?.cumulativeResults?.results.find(r => r.participant.code === participant)
  return (
    <table className={`table  table-detail ${isMobile ? "table-tiny" : "table-sm"} mt-1`}>
      <thead>
        <tr>
          <th className="text-left"><Label data="HeaderHole" /></th>
          {
            configData?.config?.extendedConfig?.filter(c => c.code === 'HOLE' && c.pos >= fromRound && c.pos <= toRound).map((conf) => {
              return (
                <th key={"header-hole" + conf.pos} className="text-center">{conf.pos}</th>
              )
            })
          }
          {(toRound === 9)
            ? (
              <th className="text-center"><Label data="HeaderOut" /></th>
            ) : (
              <>
                <th className="text-center"><Label data="HeaderIn" /></th>
                <th className="text-center"><Label data="HeaderRound" /></th>
              </>
            )
          }
        </tr>
      </thead>
      <tbody>
        {
          _units.map((unit) => {
            const unitData = unitsData.find(u => u.data?.config?.unit === unit.rsc)?.data
            const competitorData = unitData?.results?.items?.find(r => r.participant.code === participant)

            return (
              <tr key={'row-' + unit.round + '-' + participant}>
                <td className="text-left"><strong>R{unit.round}</strong></td>
                {
                  configData?.config?.extendedConfig?.filter(c => c.code === 'HOLE' && c.pos >= fromRound && c.pos <= toRound).map((conf) => {
                    const val2 = competitorData?.extendedResults?.find(r => r.er_code === 'HOLE' && r.er_pos === conf.pos)?.er_value2;
                    return (
                      <td key={'hole-' + unit.round + '-' + participant + '-' + conf.pos} className={getStrikesClass(val2)}>
                        {competitorData?.extendedResults?.find(r => r.er_code === 'HOLE' && r.er_pos === conf.pos)?.er_value}
                      </td>
                    )
                  })
                }
                {(toRound === 9)
                  ? (
                    <td className="text-center">{competitorData?.extendedResults?.find(r => r.er_code === 'FRONT')?.er_value}</td>
                  ) : (
                    <>
                      <td className="text-center">{competitorData?.extendedResults?.find(r => r.er_code === 'BACK')?.er_value}</td>
                      <td className="text-center">{competitorData?.extendedResults?.find(r => r.er_code === 'COMPLETE')?.er_value}</td>
                    </>
                  )
                }
              </tr>
            )
          })
        }
        <tr>
          {

            (toRound === 9)
              ? (
                <td colSpan="11">&nbsp;</td>
              ) : (
                <>
                  <td colSpan="10"></td>
                  <td className="text-center"><strong><Label data="HeaderTotal" /></strong></td>
                  <td className="text-center">{competitorCumulativeData?.resultData}</td>
                </>
              )
          }
        </tr>
        <tr>
          <td className="text-left"><Label data="HeaderPar" /></td>
          {
            configData?.config?.extendedConfig?.filter(c => c.code === 'HOLE' && c.pos >= fromRound && c.pos <= toRound).map((conf) => {
              return (
                <td key={'par-' + participant + '-' + conf.pos} className="text-center">{conf.value}</td>
              )
            })
          }
          {(toRound === 9)
            ? (<td></td>)
            : (<><td></td><td></td></>)
          }
        </tr>
        {(currentMetric === "metric")
          ? (
            <tr>
              <td className="text-left"><Label data="HeaderMeters" /></td>
              {
                configData?.config?.extendedConfig?.filter(c => c.code === 'HOLE' && c.pos >= fromRound && c.pos <= toRound).map((conf) => {
                  return (
                    <td key={'meters-' + participant + '-' + conf.pos} className="text-center">
                      {conf?.extension.find(e => e.code === 'DISTANCE' && e.pos === "0")?.value}
                    </td>
                  )
                })
              }
              {(toRound === 9)
                ? (<td></td>)
                : (<><td></td><td></td></>)
              }
            </tr>
          ) : (
            <tr>
              <td className="text-left"><Label data="HeaderYards" /></td>
              {
                configData?.config?.extendedConfig?.filter(c => c.code === 'HOLE' && c.pos >= fromRound && c.pos <= toRound).map((conf) => {
                  return (
                    <td key={'yards-' + participant + '-' + conf.pos} className="text-right">{conf?.extension.find(e => e.code === 'DISTANCE' && e.pos === "1")?.value}</td>
                  )
                })
              }
              {(toRound === 9)
                ? (<td></td>)
                : (<><td></td><td></td></>)
              }
            </tr>
          )}
      </tbody>
    </table>
  )
}